import type { IMenu } from '@shared/core';
import type { Breakpoints } from '../../core/models/breakpoints';
import {
  APP_STORE_LINKS,
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
} from '../constants/app-store-links.const';

/** Маршрут страницы «Оплата и правила» (прототип UIMS-Parking-1a) внутри `/<bp>/main`. */
export const PAYMENT_RULES_SLUG = 'payment-rules';
/** Query-параметр вкладки: `?tab=app|terminal|fines`. */
export const PAYMENT_RULES_TAB_PARAM = 'tab';
/** i18n-ключ заголовка страницы и пункта навигации. */
export const PAYMENT_RULES_TITLE = 'PAYMENT_RULES_TITLE';

/** CMS-разделы, которые объединены на странице. */
export const PAYMENT_METHOD_MENU_SLUG = 'payment-method';
export const FINES_MENU_SLUG = 'fines';

export type PaymentRulesTabId = 'app' | 'terminal' | 'fines';

/** Брейкпоинт страницы: 'desktop' | 'mobile' (значения enum `Breakpoints`). */
export type PaymentRulesLayout = `${Breakpoints}`;

/**
 * Пункт навигации шапки и бургера: пункт CMS-меню либо локальный пункт, у которого `title` —
 * i18n-ключ (переводится `LocalizationPipe`).
 */
export type NavMenuItem = Omit<IMenu, 'title'> & { title?: IMenu['title'] | string };

export interface IPaymentRulesTab {
  id: PaymentRulesTabId;
  /** Slug CMS-страницы с контентом вкладки (`MenuService.getMenuBySlug`). */
  slug: string;
  /** i18n-ключ полной подписи (desktop). */
  title: string;
  /** i18n-ключ короткой подписи (сегмент-контрол mobile). */
  shortTitle: string;
}

export const PAYMENT_RULES_TABS: readonly IPaymentRulesTab[] = [
  {
    id: 'app',
    slug: 'payment-using-a-mobile-app',
    title: 'PAYMENT_RULES_TAB_APP',
    shortTitle: 'PAYMENT_RULES_TAB_APP_SHORT',
  },
  {
    id: 'terminal',
    slug: 'payment-via-payment-terminals',
    title: 'PAYMENT_RULES_TAB_TERMINAL',
    shortTitle: 'PAYMENT_RULES_TAB_TERMINAL_SHORT',
  },
  {
    id: 'fines',
    slug: FINES_MENU_SLUG,
    title: 'PAYMENT_RULES_TAB_FINES',
    shortTitle: 'PAYMENT_RULES_TAB_FINES_SHORT',
  },
];

export interface IPaymentRulesCard {
  /** i18n-ключ крупного значения; на mobile — с суффиксом `_SHORT`. */
  value: string;
  /** i18n-ключ подписи; на mobile — с суффиксом `_SHORT`. */
  text: string;
  tone: 'green' | 'orange';
}

/**
 * Карточки условий над вкладками. Тексты — из действующих источников: режим работы и тариф —
 * CMS FAQ («Какой режим работы парковок?», «Сколько стоит час парковки?»), 10 минут — CMS `fines`.
 */
export const PAYMENT_RULES_CARDS: readonly IPaymentRulesCard[] = [
  { value: 'PAYMENT_RULES_CARD_HOURS_VALUE', text: 'PAYMENT_RULES_CARD_HOURS_TEXT', tone: 'green' },
  {
    value: 'PAYMENT_RULES_CARD_TARIFF_VALUE',
    text: 'PAYMENT_RULES_CARD_TARIFF_TEXT',
    tone: 'green',
  },
  { value: 'PAYMENT_RULES_CARD_EXIT_VALUE', text: 'PAYMENT_RULES_CARD_EXIT_TEXT', tone: 'orange' },
];

export const DEFAULT_PAYMENT_RULES_TAB: PaymentRulesTabId = 'app';

/** Вкладка из query-параметра; неизвестное или пустое значение — `app`. */
export function resolvePaymentRulesTab(param: string | null | undefined): PaymentRulesTabId {
  return PAYMENT_RULES_TABS.find(tab => tab.id === param)?.id ?? DEFAULT_PAYMENT_RULES_TAB;
}

/**
 * Клавиатура в `role=tablist`: ←/→ по кругу, Home/End — первая/последняя.
 * Прочие клавиши — null (событие не обрабатывается).
 */
export function nextPaymentRulesTab(
  current: PaymentRulesTabId,
  key: string,
): PaymentRulesTabId | null {
  const ids = PAYMENT_RULES_TABS.map(tab => tab.id);
  const index = ids.indexOf(current);
  switch (key) {
    case 'ArrowRight':
      return ids[(index + 1) % ids.length];
    case 'ArrowLeft':
      return ids[(index - 1 + ids.length) % ids.length];
    case 'Home':
      return ids[0];
    case 'End':
      return ids[ids.length - 1];
    default:
      return null;
  }
}

/**
 * Вопросы CMS-страницы `faq` под каждой вкладкой — по позиции (порядок одинаков в ru/kz/en и в
 * fallback `FAQ_KEYS`): 2 — пополнить счёт, 3 — как узнать, что платная, 5 — бесплатная стоянка,
 * 6 — паркомат за любую зону, 7 — оплатить одну зону, стоять в другой, 8 — фискальный чек.
 */
export const PAYMENT_RULES_FAQ_INDEXES: Readonly<Record<PaymentRulesTabId, readonly number[]>> = {
  app: [2, 8],
  terminal: [6, 7, 8],
  fines: [3, 5],
};

/** Вопросы по индексам в заданном порядке; несуществующие индексы отбрасываются. */
export function pickFaqPanels<T>(panels: readonly T[], indexes: readonly number[]): T[] {
  return indexes.filter(index => index >= 0 && index < panels.length).map(index => panels[index]);
}

/**
 * Навигация (шапка desktop, бургер mobile) поверх CMS-меню: `payment-method` заменяется пунктом
 * «Оплата и правила» на своём месте, `fines` убирается (стал вкладкой). Нет `payment-method` —
 * пункт ставится первым, сразу после «Карты парковок». Остальные пункты — в порядке CMS.
 */
export function paymentRulesNavMenus(menus: readonly IMenu[]): NavMenuItem[] {
  const paymentMethod = menus.find(menu => menu.slug === PAYMENT_METHOD_MENU_SLUG);
  const rules: NavMenuItem = {
    slug: PAYMENT_RULES_SLUG,
    title: PAYMENT_RULES_TITLE,
    icon: paymentMethod?.icon ?? 'icons:wallet',
  };

  const result = menus
    .filter(menu => menu.slug !== FINES_MENU_SLUG)
    .map((menu): NavMenuItem => (menu === paymentMethod ? rules : menu));
  return paymentMethod ? result : [rules, ...result];
}

/** Порядок кнопок магазинов на mobile — как в прототипе и QR-блоке desktop. */
const STORE_ORDER = [APP_STORE_URL, GOOGLE_PLAY_URL] as const;

/** Ссылки магазинов из `APP_STORE_LINKS` в явном порядке `STORE_ORDER`. */
export function paymentRulesStoreLinks(): (typeof APP_STORE_LINKS)[number][] {
  return STORE_ORDER.flatMap(url => APP_STORE_LINKS.filter(link => link.url === url));
}
